// AppliedTextCard.tsx
import React, { useState } from 'react';
import DOMPurify from 'dompurify';
import { apiClient } from '../../../../utils/apiClient';
import Document from '../../../icons/Document';
import QuestionCircle from '../../../icons/QuestionCircle';
import Book from '../../../icons/Book';
import Globe from '../../../icons/Globe';

interface ExtractedLegalReference {
    originalText: string;
    type: 'article' | 'section' | 'code' | 'international' | 'unknown';
    source?: 'visa' | 'ai-full-text' | 'manual'; // Source de l'extraction
    found: boolean;
    articleId?: string;
    codeId?: string;
    sectionId?: string;
    articleNum?: string;
    codeTitle?: string;
    sectionTitle?: string;
    confidence?: number;
    enrichmentDate?: string;
    isInternational?: boolean;
}

interface AppliedTextCardProps {
    reference: ExtractedLegalReference;
    onOpenLegifranceModal?: (articleId: string, codeId?: string, sectionId?: string) => void;
}

const AppliedTextCard: React.FC<AppliedTextCardProps> = ({ reference, onOpenLegifranceModal }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [articleDetails, setArticleDetails] = useState<any>(null);
    const [isLoadingDetails, setIsLoadingDetails] = useState(false);
    const [detailsError, setDetailsError] = useState<string | null>(null);

    /**
     * Récupère les détails de l'article depuis Legifrance
     */
    const fetchArticleDetails = async () => {
        if (!reference.articleId || articleDetails) return;

        setIsLoadingDetails(true);
        setDetailsError(null);

        try {
            const response = await apiClient('/legifrance/details-article', {
                method: 'POST',
                body: JSON.stringify({ articleId: reference.articleId }),
            });

            if (!response.ok) {
                throw new Error('Erreur lors de la récupération des détails');
            }

            const responseData = await response.json();
            if (responseData.success) {
                setArticleDetails(responseData.data);
            } else {
                throw new Error(responseData.error?.message || 'Erreur inconnue');
            }
        } catch (error) {
            console.error('Erreur récupération détails article:', error);
            setDetailsError('Impossible de charger les détails');
        } finally {
            setIsLoadingDetails(false);
        }
    };

    /**
     * Gère l'expansion/contraction de la carte
     */
    const handleToggleExpand = async () => {
        if (!isExpanded && reference.found && reference.articleId) {
            await fetchArticleDetails();
        }
        setIsExpanded(!isExpanded);
    };

    /**
     * Icône selon le type de référence
     */
    const getTypeIcon = () => {
        if (reference.isInternational) {
            return (
                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
            );
        }

        switch (reference.type) {
            case 'article':
                return (
                    <Document width="20px" height="20px" className="text-mcdm-secondary-400" style={{ transform: 'translateY(2px)' }} />
                );
            case 'code':
                return (
                    <Book width="20px" height="20px" className="text-mcdm-secondary-400" style={{ transform: 'translateY(2px)' }} />
                );
            case 'section':
                return (
                    <svg className="w-5 h-5 text-mcdm-secondary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                    </svg>
                );
            case 'international':
                return (
                    <Globe width="20px" height="20px" className="text-mcdm-secondary-400" style={{ transform: 'translateY(2px)' }} />
                );
            default:
                return (
                    <QuestionCircle width="20px" height="20px" className="text-gray-400" style={{ transform: 'translateY(2px)' }} />
                );
        }
    };

    /**
     * Badge de validité
     */
    const getStatusBadge = () => {
        if (reference.isInternational) {
            return (
                <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-200">
                    Texte international
                </span>
            );
        }
        if (!reference.found) {
            return (
                <span className="px-2 py-1 text-xs rounded-full bg-mcdm-primary-200 text-mcdm-third-500 dark:bg-mcdm-primary-100 dark:text-mcdm-third-500">
                    Texte indisponible
                </span>
            );
        }
        return (
            <span className="px-2 py-1 text-xs rounded-full bg-mcdm-green-100 text-mcdm-primary-100 dark:bg-green-900 dark:text-green-200">
                Texte disponible
            </span>
        );
    };

    return (
        <div className="border border-gray-200 dark:border-mcdm-third-300 rounded-lg p-3 bg-white dark:bg-mcdm-third-400">
            {/* Header toujours visible */}
            <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-2 flex-1">
                    {getTypeIcon()}
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <h4 className="font-semibold text-sm text-mcdm-third-500 dark:text-mcdm-primary-100 break-words">
                                {reference.articleNum || reference.originalText}
                            </h4>
                            {getStatusBadge()}
                            {/* Badge source IA */}
                            {reference.source === 'ai-full-text' && (
                                <span className="px-2 py-1 text-xs rounded-full bg-mcdm-secondary-100 text-mcdm-primary-100 dark:bg-mcdm-third-200 dark:text-mcdm-primary-100" title="Extrait par l'IA depuis le texte intégral">
                                    Détection automatique
                                </span>
                            )}
                        </div>
                        {reference.codeTitle && (
                            <p className="text-xs text-gray-500 dark:text-gray-400 break-words">
                                {reference.codeTitle}
                            </p>
                        )}
                    </div>
                </div>

                {/* Bouton expand/collapse */}
                {reference.found && (
                    <button
                        onClick={handleToggleExpand}
                        className="p-1 hover:bg-gray-100 dark:hover:bg-mcdm-third-300 rounded transition-colors"
                        aria-label={isExpanded ? 'Réduire' : 'Développer'}
                    >
                        {isExpanded ? (
                            <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                            </svg>
                        ) : (
                            <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                        )}
                    </button>
                )}
            </div>

            {/* Contenu étendu */}
            {isExpanded && reference.found && (
                <div className="mt-3 pt-3 border-t border-gray-200 dark:border-mcdm-third-300">
                    {isLoadingDetails && (
                        <div className="flex items-center justify-center py-4">
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-mcdm-secondary-400"></div>
                            <span className="ml-2 text-sm text-gray-500">Chargement des détails...</span>
                        </div>
                    )}

                    {detailsError && (
                        <div className="text-sm text-red-600 dark:text-red-400 py-2">
                            {detailsError}
                        </div>
                    )}

                    {articleDetails && !isLoadingDetails && (
                        <div className="space-y-3">
                            {/* Contenu de l'article */}
                            {articleDetails.article?.texteHtml && (
                                <div className="text-sm text-gray-700 dark:text-gray-300 max-h-64 overflow-y-auto">
                                    <div
                                        dangerouslySetInnerHTML={{
                                            __html: DOMPurify.sanitize(articleDetails.article.texteHtml)
                                        }}
                                    />
                                </div>
                            )}

                            {/* Informations de validité */}
                            {(articleDetails.article?.dateDebut || articleDetails.article?.etat) && (
                                <div className="text-xs text-gray-500 dark:text-gray-400 space-y-1">
                                    {articleDetails.article.etat && (
                                        <p>
                                            <span className="font-semibold">État :</span>{' '}
                                            {articleDetails.article.etat === 'VIGUEUR' ? 'En vigueur' : articleDetails.article.etat}
                                        </p>
                                    )}
                                    {articleDetails.article.dateDebut && (
                                        <p>
                                            <span className="font-semibold">Depuis le :</span>{' '}
                                            {new Date(articleDetails.article.dateDebut).toLocaleDateString('fr-FR')}
                                        </p>
                                    )}
                                </div>
                            )}

                            {/* Boutons d'action */}
                            <div className="flex gap-2 mt-3">
                                {reference.articleId && (
                                    <a
                                        href={`https://www.legifrance.gouv.fr/codes/article_lc/${reference.articleId}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-sm px-3 py-1.5 border border-gray-300 dark:border-mcdm-third-300 hover:bg-gray-50 dark:hover:bg-mcdm-third-300 rounded transition-colors text-mcdm-third-500 dark:text-mcdm-primary-100"
                                    >
                                        Voir sur Légifrance
                                    </a>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Message si texte international */}
            {reference.isInternational && (
                <p className="text-xs text-mcdm-secondary-500 dark:text-blue-400 mt-2">
                    Cette référence est un texte international (non disponible dans Légifrance)
                </p>
            )}

            {/* Message si non trouvé */}
            {!reference.found && !reference.isInternational && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                    Référence non trouvée dans Légifrance
                </p>
            )}
        </div>
    );
};

export default AppliedTextCard;
